/**
 * AudioRecorder - Hold-to-record button using MediaRecorder API.
 * GameBoy edition: pixelated, square, 8-bit feel. NO rounded corners.
 *
 * - Press and hold to record
 * - Release to stop and send the audio (via onSend callback)
 * - Auto-stops at 30 seconds
 * - iOS Safari: prefers audio/mp4 (AAC) over audio/webm (Opus)
 * - Chrome / Firefox: prefers audio/webm;codecs=opus
 * - Errors (mic permission denied, send failure) are reported via `onError`
 *   so the parent can show them in its existing error banner.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { blobToBase64 } from '@/lib/ringChat';

interface AudioRecorderProps {
  onSend: (base64: string, mime: string) => Promise<void> | void;
  /** Called when recording or sending fails. Parent should show to user. */
  onError?: (message: string) => void;
  disabled?: boolean;
}

const MAX_DURATION_MS = 60_000; // 1 min — Worker allows up to ~4 min of Opus @ 24kbps
const MIN_DURATION_MS = 250; // ignore taps shorter than this

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return 'audio/webm';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
  ];
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return 'audio/webm';
}

function describeError(err: unknown): string {
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
      return 'Sin permiso de micrófono. Habilitalo en los ajustes del navegador.';
    }
    if (err.name === 'NotFoundError') {
      return 'No se encontró micrófono en este dispositivo.';
    }
    if (err.name === 'NotReadableError') {
      return 'El micrófono está siendo usado por otra app.';
    }
  }
  return 'Error al grabar audio';
}

export function AudioRecorder({ onSend, onError, disabled }: AudioRecorderProps) {
  const [recording, setRecording] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [sending, setSending] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const mimeRef = useRef<string>('audio/webm');
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  const stopTimeoutRef = useRef<number | null>(null);
  const onSendRef = useRef(onSend);
  const onErrorRef = useRef(onError);
  onSendRef.current = onSend;
  onErrorRef.current = onError;

  const reportError = useCallback((msg: string) => {
    onErrorRef.current?.(msg);
  }, []);

  const cleanup = useCallback(() => {
    if (tickRef.current) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (stopTimeoutRef.current) {
      window.clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    recorderRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async () => {
    if (recording || sending || disabled) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      reportError('Este dispositivo no soporta grabación de audio');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      mimeRef.current = pickMimeType();
      const rec = new MediaRecorder(stream, { mimeType: mimeRef.current });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const mime = mimeRef.current;
        const duration = Date.now() - startedAtRef.current;
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        // Release the mic immediately
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        // Ignore accidental taps shorter than MIN_DURATION_MS — they'd
        // produce empty / meaningless blobs and confuse the recipient.
        if (blob.size === 0 || duration < MIN_DURATION_MS) {
          setRecording(false);
          setDurationMs(0);
          return;
        }
        setSending(true);
        try {
          const base64 = await blobToBase64(blob);
          await onSendRef.current(base64, mime);
        } catch (err) {
          console.error('AudioRecorder send failed', err);
          reportError('Error al enviar audio');
        } finally {
          setSending(false);
          setRecording(false);
          setDurationMs(0);
        }
      };
      rec.onerror = () => {
        reportError('La grabación falló');
        setRecording(false);
        setDurationMs(0);
      };
      recorderRef.current = rec;
      startedAtRef.current = Date.now();
      rec.start();
      setRecording(true);
      setDurationMs(0);
      tickRef.current = window.setInterval(() => {
        setDurationMs(Date.now() - startedAtRef.current);
      }, 100);
      // Auto-stop at MAX_DURATION_MS
      stopTimeoutRef.current = window.setTimeout(() => {
        if (recorderRef.current && recorderRef.current.state === 'recording') {
          recorderRef.current.stop();
        }
      }, MAX_DURATION_MS);
    } catch (err) {
      console.error('Mic permission denied or unavailable', err);
      reportError(describeError(err));
      cleanup();
      setRecording(false);
    }
  }, [recording, sending, disabled, cleanup, reportError]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop(); // triggers onstop
    }
  }, []);

  const formatDuration = (ms: number) => {
    const s = Math.floor(ms / 1000);
    const cs = Math.floor((ms % 1000) / 100);
    return `${s}.${cs}s`;
  };

  if (sending) {
    return (
      <button
        type="button"
        disabled
        data-testid="audio-sending"
        aria-label="Enviando audio"
        style={{
          width: 22,
          height: 22,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--gb-mid)',
          color: 'var(--gb-bg)',
          border: '2px solid var(--gb-dark)',
          fontFamily: 'var(--pixel)',
          fontSize: 6,
          cursor: 'not-allowed',
          opacity: 0.5,
          padding: 0,
          flexShrink: 0,
        }}
      >
        ...
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onMouseDown={start}
      onMouseUp={stop}
      onMouseLeave={stop}
      onTouchStart={(e) => {
        e.preventDefault();
        start();
      }}
      onTouchEnd={(e) => {
        e.preventDefault();
        stop();
      }}
      onPointerCancel={stop}
      data-testid="audio-record"
      data-recording={recording}
      aria-label={
        recording
          ? `Grabando ${formatDuration(durationMs)}`
          : 'Mantener para grabar audio'
      }
      style={{
        position: 'relative',
        width: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: recording ? 'var(--gb-deepest)' : 'var(--gb-bg)',
        color: recording ? 'var(--gb-bg)' : 'var(--gb-dark)',
        border: '2px solid var(--gb-dark)',
        fontFamily: 'var(--pixel)',
        fontSize: 7,
        cursor: disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        touchAction: 'none',
        animation: recording ? 'gb-blink 1s infinite' : 'none',
        opacity: disabled ? 0.5 : 1,
        padding: 0,
        flexShrink: 0,
      }}
    >
      {recording ? '[||]' : 'MIC'}
      {recording && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -16,
            left: '50%',
            transform: 'translateX(-50%)',
            fontFamily: 'var(--pixel)',
            fontSize: 6,
            color: 'var(--gb-deepest)',
            whiteSpace: 'nowrap',
          }}
        >
          {formatDuration(durationMs)}/{MAX_DURATION_MS / 1000}s
        </span>
      )}
    </button>
  );
}
